/**
 * Shared browser fetch for /api/* — attaches Supabase session Bearer token.
 *
 * On 401: refresh the session once and retry a single time. No loops.
 * If refresh fails, return the original 401 so the app can send the user to /login.
 */
import { getSupabaseClient } from "@/lib/supabase";

async function attachBearer(headers: Headers): Promise<void> {
  if (headers.has("Authorization")) return;
  try {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  } catch {
    // proceed without Authorization — server will 401 if required
  }
}

export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers ?? undefined);
  await attachBearer(headers);

  const response = await fetch(input, { ...init, headers });

  if (response.status !== 401) {
    return response;
  }

  // Caller already supplied Authorization — do not refresh/retry (avoid masking their intent).
  const callerSetAuth = new Headers(init?.headers ?? undefined).has("Authorization");
  if (callerSetAuth) {
    return response;
  }

  try {
    const supabase = getSupabaseClient();
    if (!supabase) return response;

    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session?.access_token) {
      return response;
    }

    const retryHeaders = new Headers(init?.headers ?? undefined);
    retryHeaders.set("Authorization", `Bearer ${data.session.access_token}`);
    return fetch(input, { ...init, headers: retryHeaders });
  } catch {
    return response;
  }
}
